/**
 * Google Translate integration for the user panel.
 *
 * The set is driven by where private-server communities actually are, not by
 * raw session counts: Metin2/Knight Online/Silkroad (tr, ro), Mu Online/Lineage
 * (pt, es, ru), Silkroad MENA (ar), plus the European and South-East Asian
 * markets grouped in LANGUAGES below.
 *
 * IT NO LONGER MIRRORS THE MARKETING SITE, and that is deliberate rather than
 * drift. The panel is machine-translated by Google at runtime, so a language
 * costs one line here and nothing afterwards. The marketing site
 * (tlb-landing-nextjs/landing/nuxt.config.ts) ships HAND-WRITTEN locale files
 * via nuxt-i18n — adding a language there means writing and then maintaining a
 * full translation, which is a different decision with a different cost. The
 * two lists are allowed to differ; the landing site is the smaller set.
 *
 * Google's own widget UI is hidden (see _google-translate.css) and replaced by
 * LanguageSwitcher.vue, so the control matches the panel's design system.
 */

export const PAGE_LANGUAGE = 'en'

/*
 * Ordered by how much of the private-server world each one covers, not
 * alphabetically — the switcher renders this order, and the languages most
 * people here actually read should not be below the fold.
 *
 * A language costs one line: Google translates the panel on the fly, so there
 * is no file to write and nothing to keep in sync as the UI changes. That is
 * NOT true of the marketing site, which ships hand-written locale files — see
 * the note at the top of this file.
 */
export const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', dir: 'ltr' },

  // The original set: Metin2/Knight Online/Silkroad (tr, ro),
  // Mu Online/Lineage (pt, es, ru), Silkroad MENA (ar).
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷', dir: 'ltr' },
  { code: 'ro', name: 'Română', flag: '🇷🇴', dir: 'ltr' },
  { code: 'pt', name: 'Português (BR)', flag: '🇧🇷', dir: 'ltr' },
  { code: 'es', name: 'Español', flag: '🇪🇸', dir: 'ltr' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺', dir: 'ltr' },

  /*
   * Europe. The same reasoning that picked tr/ro applies to these: Metin2 has
   * long-running national communities in Germany, Poland, France, Italy and
   * Hungary, and Poland additionally carries most of the Tibia private-server
   * scene. These are toplist-voting markets, not general web traffic.
   */
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱', dir: 'ltr' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹', dir: 'ltr' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺', dir: 'ltr' },

  /*
   * South-East Asia, where the Ragnarok / Mu Online / Lineage private-server
   * scene is concentrated. Least certain of the three groups — if the analytics
   * do not show these, they are the first to cut.
   */
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳', dir: 'ltr' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭', dir: 'ltr' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩', dir: 'ltr' },

  // RTL. Kept last because `dir: 'rtl'` flips the whole document (see
  // _translate-rtl.css) and it is the only entry that does.
  { code: 'ar', name: 'العربية', flag: '🇸🇦', dir: 'rtl' },
]

const CODES = LANGUAGES.map((l) => l.code)

/** Google's own persistence cookie. Format: `/<source>/<target>`. */
const GOOGTRANS = 'googtrans'

const readCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

const isLocalhost = () =>
  ['localhost', '127.0.0.1'].includes(window.location.hostname)

const writeCookie = (name, value, domain) => {
  const oneYear = 60 * 60 * 24 * 365
  const domainPart = domain ? `; domain=${domain}` : ''
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${oneYear}${domainPart}; SameSite=Lax`
}

const deleteCookie = (name, domain) => {
  const domainPart = domain ? `; domain=${domain}` : ''
  document.cookie = `${name}=; path=/; max-age=0${domainPart}`
}

/** Active target language, read back from Google's cookie. */
export const currentLanguage = () => {
  const raw = readCookie(GOOGTRANS)
  if (!raw) return PAGE_LANGUAGE
  const target = raw.split('/')[2]
  return CODES.includes(target) ? target : PAGE_LANGUAGE
}

export const languageMeta = (code) =>
  LANGUAGES.find((l) => l.code === code) ?? LANGUAGES[0]

/** Reflects the active language on <html> so CSS and screen readers agree. */
const applyDocumentLanguage = (code) => {
  const meta = languageMeta(code)
  document.documentElement.setAttribute('lang', code)
  document.documentElement.setAttribute('dir', meta.dir)
  document.documentElement.classList.toggle('rtl', meta.dir === 'rtl')

  // App.vue sizes the body padding that reserves space for the sidebar, and
  // that padding has to move to the other side in RTL. It recomputes on
  // `resize`, so reuse that instead of coupling this module to the app shell.
  window.dispatchEvent(new Event('resize'))
}

export const setLanguage = (code) => {
  const target = CODES.includes(code) ? code : PAGE_LANGUAGE

  // Write on both the bare host and the shared parent domain: the parent-domain
  // cookie is what carries the choice between toplistbot.com and the panel.
  // Stale cookies at either scope win over a new one, so clear both first.
  deleteCookie(GOOGTRANS, null)
  if (!isLocalhost()) {
    deleteCookie(GOOGTRANS, '.toplistbot.com')
    deleteCookie(GOOGTRANS, `.${window.location.hostname}`)
  }

  if (target !== PAGE_LANGUAGE) {
    const value = `/${PAGE_LANGUAGE}/${target}`
    writeCookie(GOOGTRANS, value, null)
    if (!isLocalhost()) writeCookie(GOOGTRANS, value, '.toplistbot.com')
  }

  applyDocumentLanguage(target)

  // Driving Google's hidden <select> re-translates in place, with no reload.
  const combo = document.querySelector('.goog-te-combo')
  if (combo) {
    combo.value = target
    combo.dispatchEvent(new Event('change'))
    // Switching back to the source language only fully restores the original
    // text after a reload; Google leaves partially-translated nodes otherwise.
    if (target === PAGE_LANGUAGE) window.location.reload()
    return
  }

  // Widget not ready (script blocked or still loading): the cookie alone is
  // enough once the page reloads.
  window.location.reload()
}

let initialised = false

export const initGoogleTranslate = () => {
  if (initialised) return
  initialised = true

  applyDocumentLanguage(currentLanguage())

  if (document.getElementById('google-translate-script')) return

  // The widget calls this global once its script finishes loading.
  window.googleTranslateElementInit = () => {
    if (!window.google?.translate?.TranslateElement) return
    new window.google.translate.TranslateElement(
      {
        pageLanguage: PAGE_LANGUAGE,
        includedLanguages: CODES.join(','),
        autoDisplay: false,
      },
      'google_translate_element'
    )
  }

  const script = document.createElement('script')
  script.id = 'google-translate-script'
  script.src =
    'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
  script.async = true
  document.head.appendChild(script)
}
